/**
 * Configuration i18n partagée DooDates (react-i18next).
 *
 * ARCHITECTURE : `@core` est un alias *source* — chaque app Vite bundle ce
 * fichier à neuf, donc il existe UNE instance i18next PAR app (pas de singleton
 * cross-app). C'est voulu : aucun couplage runtime entre apps.
 *
 * - Namespaces *partagés* déclarés ici : common, auth, errors, legal,
 *   creator, empty_state.
 * - Les namespaces *produit* (ex: `polls`) sont enregistrés par chaque app via
 *   `registerNamespace()` pour éviter que toutes les apps bundlent les strings
 *   des autres.
 * - Init synchrone (resources inline), pas de http-backend pour 2 langues.
 * - Guard `isInitialized` contre la double-init.
 */
import i18n, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import frCommon from "./locales/fr/common.json";
import enCommon from "./locales/en/common.json";
import frAuth from "./locales/fr/auth.json";
import enAuth from "./locales/en/auth.json";
import frErrors from "./locales/fr/errors.json";
import enErrors from "./locales/en/errors.json";
import frLegal from "./locales/fr/legal.json";
import enLegal from "./locales/en/legal.json";
import frCreator from "./locales/fr/creator.json";
import enCreator from "./locales/en/creator.json";
import frEmptyState from "./locales/fr/empty_state.json";
import enEmptyState from "./locales/en/empty_state.json";
import esCommon from "./locales/es/common.json";
import esAuth from "./locales/es/auth.json";
import esErrors from "./locales/es/errors.json";
import esLegal from "./locales/es/legal.json";
import esCreator from "./locales/es/creator.json";
import esEmptyState from "./locales/es/empty_state.json";

export type AppLocale = "fr" | "en" | "es";

export const SUPPORTED_LOCALES: AppLocale[] = ["fr", "en", "es"];
export const DEFAULT_LOCALE: AppLocale = "en";
export const STORAGE_KEY = "doodates_lang";

/** Tag Intl/BCP-47 pour une locale app (formatage dates/nombres/devise). */
export function localeTag(locale: string = i18n.language): string {
  if (locale.startsWith("en")) return "en-US";
  if (locale.startsWith("es")) return "es-ES";
  return "fr-FR";
}

const resources: Resource = {
  fr: {
    common: frCommon,
    auth: frAuth,
    errors: frErrors,
    legal: frLegal,
    creator: frCreator,
    empty_state: frEmptyState,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    legal: enLegal,
    creator: enCreator,
    empty_state: enEmptyState,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    errors: esErrors,
    legal: esLegal,
    creator: esCreator,
    empty_state: esEmptyState,
  },
};

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: SUPPORTED_LOCALES,
      nonExplicitSupportedLngs: true,
      defaultNS: "common",
      ns: ["common", "auth", "errors", "legal", "creator", "empty_state"],
      detection: {
        // querystring d'abord : les liens hreflang émis par SEOHead pointent
        // vers ?lang=xx — ils doivent réellement basculer la langue.
        order: ["querystring", "localStorage", "navigator"],
        lookupQuerystring: "lang",
        lookupLocalStorage: STORAGE_KEY,
        caches: ["localStorage"],
      },
      interpolation: { escapeValue: false },
      // Rendu sûr pendant la migration incrémentale : namespaces partiellement
      // vides → renvoie la clé (pas de crash, pas de Suspense).
      react: { useSuspense: false },
      returnEmptyString: false,
    });
}

/** Enregistre un namespace produit (appelé par chaque app). */
export function registerNamespace(
  ns: string,
  bundles: Partial<Record<AppLocale, Record<string, unknown>>>,
): void {
  for (const locale of SUPPORTED_LOCALES) {
    const bundle = bundles[locale];
    if (bundle) i18n.addResourceBundle(locale, ns, bundle, true, true);
  }
}

/** Change la langue active et met à jour <html lang>. */
export function setLocale(locale: AppLocale): Promise<unknown> {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
  return i18n.changeLanguage(locale);
}

// Garder <html lang> synchro avec la langue active.
if (typeof document !== "undefined") {
  document.documentElement.lang = (i18n.language || DEFAULT_LOCALE).slice(0, 2);
  i18n.on("languageChanged", (lng) => {
    document.documentElement.lang = lng.slice(0, 2);
  });
}

// Détect missing translation keys at runtime (raw keys on screen)
if (typeof window !== "undefined") {
  i18n.on("missingKey", (lng, ns, key) => {
    const fullKey = `${ns}.${key}`;
    console.error(`❌ i18n missing key: ${fullKey} (lang: ${lng})`);
    // In CI/test, fail loud
    if (typeof process !== "undefined" && process.env?.CI) {
      throw new Error(`Missing i18n key: ${fullKey}`);
    }
  });
}

export default i18n;
